import { queryOptions } from "@tanstack/react-query";
import type { Page } from "@/lib/api/client";
import { STALE } from "@/lib/api/query-client";
import { keys } from "@/lib/api/query-keys";
import { adminApi } from "./admin.api";
import { LEGAL_ENTITY_SUBJECT_TYPE, legalEntityLogoApi } from "./legal-entity-logo";
import { organizationApi } from "./organization.api";
import { configurationApi, type ConfigListFilters } from "./configuration.api";
import { serverStatusApi } from "./server-status.api";
import type { AssignmentFilters, RoleFilters, ScopedListFilters, UserFilters } from "./admin.types";

/* ------------------------------------------------------------------ users */

export const usersQuery = (filters: UserFilters) =>
  queryOptions({
    queryKey: keys.identity.users.list(filters),
    queryFn: () => adminApi.users.list(filters),
  });

/** «Kullanıcı değiştir»in listesi — ad, birim, roller; kendiniz ve yönetim hesapları sunucuda düşülüyor. */
export const impersonationCandidatesQuery = (filters: { q?: string | undefined; limit?: number | undefined }) =>
  queryOptions({
    queryKey: keys.identity.users.candidates(filters),
    queryFn: () => adminApi.impersonation.candidates(filters),
  });

export const userQuery = (id: string | null) =>
  queryOptions({
    queryKey: keys.identity.users.detail(id ?? ""),
    queryFn: () => adminApi.users.get(id ?? ""),
    enabled: id !== null,
    staleTime: STALE.record,
  });

/** The second factor of one account, for the user drawer (17 Ekim 2026, #31). */
export const userMfaQuery = (id: string | null) =>
  queryOptions({
    queryKey: keys.identity.users.mfa(id ?? ""),
    queryFn: () => adminApi.users.mfaStatus(id ?? ""),
    enabled: id !== null,
    staleTime: STALE.record,
  });

/* ------------------------------------------------------------------ roles */

export const rolesQuery = (filters: RoleFilters) =>
  queryOptions({
    queryKey: keys.authorization.roles.list(filters),
    queryFn: () => adminApi.roles.list(filters),
  });

/**
 * Kısıtlı yetki kuralları. Nadiren değişir (katalog kodda, rol ataması oturumda) ama SİZE
 * bağlı: `callerIsSystemAdmin` çağıranın kendi hâli, o yüzden oturum önbelleğiyle aynı ömürde.
 */
export const grantRulesQuery = () =>
  queryOptions({
    queryKey: keys.authorization.grantRules(),
    queryFn: () => adminApi.roles.grantRules(),
    staleTime: STALE.record,
  });

export const roleQuery = (id: string | null) =>
  queryOptions({
    queryKey: keys.authorization.roles.detail(id ?? ""),
    queryFn: () => adminApi.roles.get(id ?? ""),
    enabled: id !== null,
    staleTime: STALE.record,
  });

/** Defined in code and seeded at boot — it changes with a deploy, not with a click. */
export const permissionCatalogQuery = () =>
  queryOptions({
    queryKey: keys.authorization.roles.catalog(),
    queryFn: () => adminApi.roles.catalog(),
    staleTime: STALE.reference,
  });

/* ------------------------------------------------------------------ assignments */

export const assignmentsQuery = (filters: AssignmentFilters) =>
  queryOptions({
    queryKey: keys.authorization.assignments.list(filters),
    queryFn: () => adminApi.assignments.list(filters),
  });

export const effectiveAccessQuery = (userId: string | null) =>
  queryOptions({
    queryKey: keys.authorization.assignments.effective(userId ?? ""),
    queryFn: () => adminApi.assignments.effective(userId ?? ""),
    enabled: userId !== null,
  });

/* ------------------------------------------------------------------ modules */

export const modulesQuery = () =>
  queryOptions({
    queryKey: keys.configuration.modules(),
    queryFn: () => adminApi.modules.list(),
    staleTime: STALE.reference,
  });

/**
 * `STALE.reference` like the module list it derives from: the answer changes only when
 * somebody switches a module on or assigns a department head, and both invalidate the
 * `configuration.modules` prefix this key sits under.
 */
export const departmentsQuery = () =>
  queryOptions({
    queryKey: keys.configuration.departments(),
    queryFn: () => adminApi.modules.departments(),
    staleTime: STALE.reference,
  });

/* ------------------------------------------------------------------ server status */

/** Ekranın «N saniyede bir yenilenir» cümlesi de buradan okuyor — sayı iki yerde yazılmasın. */
export const SERVER_STATUS_REFRESH_SECONDS = 30;

/**
 * «Sunucu Durumu». Bir KAYIT değil bir GÖSTERGE: açık bırakılıp arada bakılan bir ekran. O
 * yüzden kendiliğinden yenileniyor ve her açılışta taze ölçüm istiyor (`staleTime: 0`) —
 * elle yenilenmesi gereken bir gösterge yanlış bir anı gösterir ve bunu söylemez.
 */
export const serverStatusQuery = () =>
  queryOptions({
    queryKey: keys.system.serverStatus(),
    queryFn: () => serverStatusApi.get(),
    refetchInterval: SERVER_STATUS_REFRESH_SECONDS * 1000,
    staleTime: 0,
  });

/* ------------------------------------------------------------------ organization pickers */

export const legalEntitiesQuery = (filters: { q?: string | undefined; limit?: number | undefined } = {}) =>
  queryOptions({
    queryKey: keys.organization.legalEntities(filters),
    queryFn: () =>
      adminApi.organization.legalEntities({ limit: 100, sortBy: "legalName", sortOrder: "asc", ...filters }),
    staleTime: STALE.record,
  });

export const sitesQuery = (legalEntityId: string | null) =>
  queryOptions({
    queryKey: keys.organization.sites(legalEntityId),
    queryFn: () =>
      adminApi.organization.sites({ legalEntityId: legalEntityId ?? undefined, status: "ACTIVE", limit: 100, sortBy: "name", sortOrder: "asc" }),
    enabled: legalEntityId !== null,
    staleTime: STALE.record,
  });

export const orgUnitsQuery = (legalEntityId: string | null) =>
  queryOptions({
    queryKey: keys.organization.orgUnits(legalEntityId),
    queryFn: () =>
      adminApi.organization.orgUnits({ legalEntityId: legalEntityId ?? undefined, status: "ACTIVE", limit: 100 }),
    enabled: legalEntityId !== null,
    staleTime: STALE.record,
  });

export const warehousesQuery = (legalEntityId: string | null) =>
  queryOptions({
    queryKey: keys.inventory.warehouses(legalEntityId),
    queryFn: () =>
      adminApi.warehouses.list({ legalEntityId: legalEntityId ?? undefined, limit: 100, sortBy: "name", sortOrder: "asc" }),
    enabled: legalEntityId !== null,
    staleTime: STALE.record,
  });

/* ------------------------------------------------------------------ Kuruluş resources */


/**
 * One query per Kuruluş resource, built the same way.
 *
 * Paging keeps the previous rows (the client default, `query-client.ts`); a search or filter
 * change shows the loading state instead — the old rows belong to another list (31 Ekim 2026).
 */
function resourceQuery<TRow>(
  key: readonly unknown[],
  list: (filters: ScopedListFilters) => Promise<Page<TRow>>,
) {
  return (filters: ScopedListFilters) =>
    queryOptions({
      queryKey: [...key, filters] as const,
      queryFn: () => list(filters),
      staleTime: STALE.record,
    });
}

export const legalEntityListQuery = resourceQuery(["organization", "legal-entities"], (f) =>
  organizationApi.legalEntities.list(f),
);
export const siteListQuery = resourceQuery(["organization", "sites"], (f) =>
  organizationApi.sites.list(f),
);
export const orgUnitListQuery = resourceQuery(["organization", "org-units"], (f) =>
  organizationApi.orgUnits.list(f),
);
export const jobTitleListQuery = resourceQuery(["organization", "job-titles"], (f) =>
  organizationApi.jobTitles.list(f),
);
export const positionListQuery = resourceQuery(["organization", "positions"], (f) =>
  organizationApi.positions.list(f),
);
export const costCenterListQuery = resourceQuery(["organization", "cost-centers"], (f) =>
  organizationApi.costCenters.list(f),
);
export const numberSequenceListQuery = resourceQuery(["organization", "number-sequences"], (f) =>
  organizationApi.numberSequences.list(f),
);

/* ------------------------------------------------------------------ configuration */

export const lookupTypeListQuery = (filters: ConfigListFilters) =>
  queryOptions({
    queryKey: ["configuration", "lookup-types", filters] as const,
    queryFn: () => configurationApi.lookupTypes.list(filters),
    staleTime: STALE.reference,
  });

export const lookupValueListQuery = (filters: ConfigListFilters) =>
  queryOptions({
    queryKey: ["configuration", "lookup-values", filters] as const,
    queryFn: () => configurationApi.lookupValues.list(filters),
    staleTime: STALE.reference,
  });

export const fieldGroupListQuery = (filters: ConfigListFilters) =>
  queryOptions({
    queryKey: ["configuration", "field-groups", filters] as const,
    queryFn: () => configurationApi.fieldGroups.list(filters),
    staleTime: STALE.reference,
  });

export const fieldDefinitionListQuery = (filters: ConfigListFilters) =>
  queryOptions({
    queryKey: ["configuration", "field-definitions", filters] as const,
    queryFn: () => configurationApi.fieldDefinitions.list(filters),
    staleTime: STALE.reference,
  });

export const fieldPolicyListQuery = (filters: ConfigListFilters) =>
  queryOptions({
    queryKey: ["configuration", "field-policies", filters] as const,
    queryFn: () => configurationApi.fieldPolicies.list(filters),
    staleTime: STALE.reference,
  });

export const settingListQuery = (filters: ConfigListFilters) =>
  queryOptions({
    queryKey: ["configuration", "settings", filters] as const,
    queryFn: () => configurationApi.settings.list(filters),
    staleTime: STALE.record,
  });

export const storageTargetListQuery = (filters: ConfigListFilters) =>
  queryOptions({
    queryKey: ["configuration", "storage-targets", filters] as const,
    queryFn: () => configurationApi.storageTargets.list(filters),
    staleTime: STALE.reference,
  });

/**
 * Şirket logosu için yükleme yuvası kataloğu.
 *
 * `enabled`, çağıranın `files.file.read` yetkisi. Dağıtımla değişen referans verisi, o yüzden
 * referans verisi gibi önbellekleniyor; hiç gelmediğinde seçici belgelenmiş sınırlara düşüyor
 * (`logoConstraintsFrom`) — ekranın onsuz da çalıştığı bir katalog, üç deneme ve sekiz saniye
 * boyunca seçiciyi kullanılmaz bırakmamalı.
 */
export const legalEntityLogoPurposesQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: ["files", "purposes", LEGAL_ENTITY_SUBJECT_TYPE] as const,
    queryFn: () => legalEntityLogoApi.purposes(),
    enabled,
    staleTime: STALE.reference,
    retry: false,
  });
